import { BodySemi, SmallBodyRegular } from 'components/Text';
import {
    DataText,
    PricePointCell,
    TableClusterIcon,
    TableHealthStatLabel,
    TableHealthStatLabelInactive,
} from './styledComponents';
import ClusterIcon from 'components/ClusterIcon';
import FlavorsCell from './FlavorsCell';
import HealthStatTooltip from '../../HealthStatTooltip';
import Link from '../../Link';
import { OverflowEllipsis } from 'utils/fragments';
import { SpateMetricMetadata } from 'utils/metrics';
import Stack from 'components/Stack';
import Tooltip from 'components/Tooltip';
import { getPathFromNode } from 'utils/helpers';
import styled from 'styled-components';

const pricePointHealthStatAccessor = ({ healthStats }) => {
    if (
        healthStats &&
        healthStats['price point'] &&
        (healthStats['price point'].score === 0 ||
            healthStats['price point'].score > 0)
    ) {
        return healthStats['price point'].scoreLabel;
    }
    return '';
};

const clusterFormatCell = ({ value }) =>
    value ? <ClusterIcon cluster={value} css="font-size: 24px;" /> : '\u2013';

const ParentName = styled(SmallBodyRegular)`
    white-space: normal;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const parentNameFormatCell = ({ value }) => (
    <ParentName>
        {Array.isArray(value) ? value.join(', ') : value || '\u2013'}
    </ParentName>
);

const OverflowBodySemi = styled(BodySemi)`
    ${OverflowEllipsis}
`;

export const nameFormatCell = ({ value, row, nameToolTipContent, nameCss }) => (
    <Stack
        $direction="row"
        $space="0.25rem"
        $wrap={false}
        css={'min-width: 0; width: 100%; justify-content: space-between;'}
    >
        {nameToolTipContent ? (
            <Tooltip content={nameToolTipContent}>
                <OverflowBodySemi
                    css={`
                        text-transform: capitalize;
                        ${nameCss}
                    `}
                >
                    {value}
                </OverflowBodySemi>
            </Tooltip>
        ) : (
            <OverflowBodySemi css="text-transform: capitalize;">
                {value}
            </OverflowBodySemi>
        )}
        <TableClusterIcon cluster={row.cluster} fetching={row.fetchingData} />
    </Stack>
);

const pricePointFormatCell = ({ row }) => (
    <PricePointCell
        score={row.healthStats?.['price point']?.score}
        scoreLabel={row.healthStats?.['price point']?.scoreLabel}
    />
);

const DEFAULT_MIN_WIDTH = 5;
const DEFAULT_WIDTH = '8.625rem';
const defaultFormatCell =
    (config) =>
    ({ value }) => (
        <DataText
            css={`
                font-size: 0.875rem;
            `}
        >
            {config.format(value)}
        </DataText>
    );

const createTimestampFormatCell =
    (config) =>
    ({ value }) => (
        <span
            css={`
                font-size: 0.75rem;
            `}
        >
            {config.format(value)}
        </span>
    );

const BrandWrapper = styled.span`
    white-space: normal;
    font-size: 0.825rem;
    text-transform: capitalize;
    text-align: right;
    line-height: 1.2;
`;

const BrandLink = styled(Link)`
    color: ${({ theme }) => theme.purple_1};
    text-decoration: none;

    &:hover,
    &:focus-visible {
        text-decoration: underline;
    }
`;

const createBrandNameFormatCell =
    (_config) =>
    ({ row }) => (
        <BrandWrapper>
            <BrandLink
                to={getPathFromNode({
                    id: row.brand,
                    type: 'brand',
                    name: row.brandName,
                })}
            >
                {row.brandName}
            </BrandLink>
        </BrandWrapper>
    );

const healthStatLabelsFormatCell = (config) => {
    if (!config.healthStatKey) return defaultFormatCell(config);
    return ({ value, row, active, selected }) => {
        const healthStats = row?.healthStats;
        const maybeHealthStat = healthStats?.[config.healthStatKey];
        const { scoreLabel, score, scoreLabelShort } = maybeHealthStat || {};
        const HealthStatLabelComponent =
            active || selected || config.activeHealthStatLabel
                ? TableHealthStatLabel
                : TableHealthStatLabelInactive;
        return (
            <span
                css={`
                    font-size: 0.875rem;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                `}
            >
                {scoreLabelShort ? (
                    <HealthStatTooltip
                        metricLabel={config.healthStatKey}
                        label={scoreLabel}
                    >
                        <HealthStatLabelComponent
                            $metricLabel={config.healthStatKey}
                            $score={score}
                        >
                            {scoreLabelShort}
                        </HealthStatLabelComponent>
                    </HealthStatTooltip>
                ) : null}
                <DataText>{config.format(value)}</DataText>
            </span>
        );
    };
};

const flavorsFormatCell = (config) => {
    return ({ row }) => {
        return <FlavorsCell item={row} />;
    };
};

const defaultOptions = {
    showHealthStatLabels: false,
};
export const createDefaultColumns = (
    columnsConfig,
    options = defaultOptions,
) => {
    return columnsConfig.map((config) => {
        if (config instanceof SpateMetricMetadata) {
            const overrides = {
                formatCell: defaultFormatCell(config),
            };
            if (config.key === 'price_segmentation') {
                overrides.formatCell = pricePointFormatCell;
                overrides.accessor = pricePointHealthStatAccessor;
            } else if (config.key === 'flavors') {
                overrides.formatCell = flavorsFormatCell(config);
            } else if (config.key === 'parentName') {
                overrides.formatCell = parentNameFormatCell;
            } else if (options.showHealthStatLabels) {
                overrides.formatCell = healthStatLabelsFormatCell(config);
            } else if (config.key === 'create_timestamp') {
                overrides.formatCell = createTimestampFormatCell(config);
            } else if (config.key === 'brandName') {
                overrides.formatCell = createBrandNameFormatCell(config);
            }
            return config.getTableColumnConfig(overrides);
        }

        if (typeof config !== 'string')
            throw new Error(`Invalid column config: ${config}`);

        switch (config) {
            case 'name':
                return {
                    key: config,
                    label: 'a-z',
                    $minWidth: 17,
                    $width: '45%',
                    formatCell: nameFormatCell,
                };
            case 'parentName':
                return {
                    key: config,
                    label: 'group',
                    $minWidth: 9,
                    $width: DEFAULT_WIDTH,
                    formatCell: parentNameFormatCell,
                };
            case 'cluster':
                return {
                    key: config,
                    $minWidth: DEFAULT_MIN_WIDTH,
                    $width: DEFAULT_WIDTH,
                    formatCell: clusterFormatCell,
                };
            default:
                throw new Error(
                    `Could not create default column for key "${config}"`,
                );
        }
    });
};
